import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from "@/lib/language";
import { content, type Lang } from "@/lib/content";

export interface FaqItem {
  q: Record<Lang, string>;
  a: Record<Lang, string>;
}

/**
 * Renders a visible FAQ section. The exact same Q&As must be emitted as
 * FAQPage JSON-LD in the route head (see faqItemsFor / faqPage) — Google drops
 * mismatched rich results.
 */
export function FaqSection({
  items,
  eyebrow = content.faq.eyebrow,
  title = content.faq.title,
  className = "bg-background",
}: {
  items: readonly FaqItem[];
  eyebrow?: Record<Lang, string>;
  title?: Record<Lang, string>;
  className?: string;
}) {
  const { t } = useLanguage();
  if (!items?.length) return null;

  return (
    <section className={className}>
      <div className="mx-auto max-w-3xl px-4 lg:px-8 py-24 sm:py-28">
        <div className="text-center">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            {t(eyebrow)}
          </span>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {t(title)}
          </h2>
        </div>

        <Accordion type="single" collapsible className="mt-12 w-full">
          {items.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-border">
              <AccordionTrigger className="gap-4 text-start text-base font-semibold text-foreground hover:text-primary hover:no-underline">
                {t(item.q)}
              </AccordionTrigger>
              <AccordionContent
                forceMount
                className="text-start text-sm leading-relaxed text-muted-foreground"
              >
                {t(item.a)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
